import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BUTTON,
  activationTarget,
  chooseSpatialTarget,
  isGamepadFocusable,
  listFocusables,
  subscribeGamepad,
  type Direction,
  type RectLike,
} from './gamepad';
import {
  combatActions,
  combatActivate,
  combatActorFocus,
  combatBack,
  combatCards,
  combatEndHoldAction,
  combatHome,
  combatMoveTarget,
  focusAfterBurn,
  combatResolveFollow,
  combatSideEntry,
  isCombatAiming,
  pickedCombatAction,
  type CombatStep,
} from './combatGamepad';
import {
  focusAfterSheetAction,
  nextRosterRow,
  overviewActivate,
  overviewBack,
  overviewMoveTarget,
  selectedRosterRow,
  sheetStops,
  type OverviewStep,
  type SheetAnchor,
} from './overviewGamepad';
import './gamepad.css';

const FOCUS_CLASS = 'gamepad-focus';
const FOCUS_SELECTOR = `.${FOCUS_CLASS}, [data-gamepad-current="true"]`;

function clearMarker(node: Element) {
  node.classList.remove(FOCUS_CLASS);
  node.removeAttribute('data-gamepad-current');
}

function markFocused(element: HTMLElement) {
  element.classList.add(FOCUS_CLASS);
  element.setAttribute('data-gamepad-current', 'true');
}
const SCROLL_STEP = 18;
const HINT_TEXT = 'Controller verbunden';

export type GamepadNavOptions = {
  regions?: string[];
  profile?: 'default' | 'overview' | 'combat';
  hint?: string;
};

type OptionsSource = () => GamepadNavOptions;

function rectOf(element: HTMLElement): RectLike {
  const rect = element.getBoundingClientRect();
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

function isShown(element: HTMLElement): boolean {
  if (!element.isConnected) return false;
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  const rect = element.getBoundingClientRect();
  return rect.width >= 1 || rect.height >= 1;
}

function clearFocusRing(root: HTMLElement) {
  root.querySelectorAll(FOCUS_SELECTOR).forEach((node) => clearMarker(node));
}

function currentFocus(root: HTMLElement): HTMLElement | null {
  const marked = root.querySelector(FOCUS_SELECTOR);
  if (marked instanceof HTMLElement && marked.isConnected && isGamepadFocusable(marked)) return marked;
  if (marked) clearMarker(marked);
  const active = document.activeElement;
  if (active instanceof HTMLElement && root.contains(active) && isGamepadFocusable(active)) return active;
  return null;
}

export function setGamepadFocus(element: HTMLElement) {
  const root = element.closest('[data-gamepad-root]') ?? document;
  root.querySelectorAll(FOCUS_SELECTOR).forEach((node) => clearMarker(node));
  markFocused(element);
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
  if (!element.matches('[data-gamepad-roster-row]')) {
    element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

function trappingOverlays(root: HTMLElement): HTMLElement[] {
  const overlays = Array.from(root.querySelectorAll<HTMLElement>('[data-gamepad-overlay]'));
  return overlays.filter((overlay) => {
    if (!isShown(overlay)) return false;
    const kind = overlay.getAttribute('data-gamepad-overlay');
    if (kind === 'modal' || kind === 'catalog') return true;
    const backdrop = root.querySelector(`[data-gamepad-overlay-backdrop="${kind}"]`);
    return backdrop instanceof HTMLElement && isShown(backdrop);
  }).sort((a, b) => {
    const za = Number(window.getComputedStyle(a).zIndex);
    const zb = Number(window.getComputedStyle(b).zIndex);
    const aRank = Number.isFinite(za) ? za : 0;
    const bRank = Number.isFinite(zb) ? zb : 0;
    if (aRank !== bRank) return aRank - bRank;
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });
}

function activeScope(root: HTMLElement): HTMLElement {
  const traps = trappingOverlays(root);
  return traps.length > 0 ? traps[traps.length - 1] : root;
}

function firstFocusable(scope: ParentNode): HTMLElement | null {
  return listFocusables(scope)[0] ?? null;
}

function preferredStart(root: HTMLElement, scope: HTMLElement, options: GamepadNavOptions): HTMLElement | null {
  if (scope !== root) return entryTarget(scope);
  for (const id of usableRegionIds(root, options)) {
    const region = regionElement(root, id);
    if (!region) continue;
    const entry = region.querySelector<HTMLElement>('[data-gamepad-entry]');
    if (entry) {
      if (isGamepadFocusable(entry)) return entry;
      const inner = firstFocusable(entry);
      if (inner) return inner;
    }
    const inside = firstFocusable(region);
    if (inside) return inside;
  }
  return firstFocusable(scope);
}

function entryTarget(container: HTMLElement): HTMLElement | null {
  const entries = Array.from(container.querySelectorAll<HTMLElement>('[data-gamepad-entry]'));
  for (const entry of entries) {
    if (isGamepadFocusable(entry)) return entry;
    const inner = firstFocusable(entry);
    if (inner) return inner;
  }
  return firstFocusable(container);
}

function focusInside(container: HTMLElement) {
  const target = entryTarget(container);
  if (target) setGamepadFocus(target);
}

function regionElement(root: HTMLElement, id: string): HTMLElement | null {
  const element = root.querySelector<HTMLElement>(`[data-gamepad-region="${id}"]`);
  if (element && isShown(element)) return element;
  return null;
}

function usableRegionIds(root: HTMLElement, options: GamepadNavOptions): string[] {
  const ids = options.regions && options.regions.length > 0
    ? options.regions
    : Array.from(root.querySelectorAll<HTMLElement>('[data-gamepad-region]'))
      .map((element) => element.getAttribute('data-gamepad-region') || '')
      .filter(Boolean);
  return ids.filter((id) => regionElement(root, id) || root.querySelector(`[data-gamepad-open-region="${id}"]`));
}

function enterRegion(root: HTMLElement, id: string) {
  const region = regionElement(root, id);
  if (region) {
    focusInside(region);
    return;
  }
  const opener = root.querySelector<HTMLElement>(`[data-gamepad-open-region="${id}"]`);
  if (!opener || opener.hasAttribute('disabled')) return;
  opener.click();
  const opened = regionElement(root, id);
  if (opened) {
    focusInside(opened);
    return;
  }
  window.requestAnimationFrame(() => {
    if (!root.isConnected) return;
    const later = regionElement(root, id);
    if (later) focusInside(later);
  });
}

function cycleRegion(root: HTMLElement, options: GamepadNavOptions, delta: 1 | -1) {
  const trap = activeScope(root);
  if (trap !== root && trap.getAttribute('data-gamepad-overlay') === 'modal') {
    focusInside(trap);
    return;
  }
  const ids = usableRegionIds(root, options);
  if (ids.length === 0) return;
  const focus = currentFocus(root);
  const currentId = focus?.closest('[data-gamepad-region]')?.getAttribute('data-gamepad-region') ?? null;
  const index = currentId ? ids.indexOf(currentId) : -1;
  const nextIndex = index === -1
    ? (delta > 0 ? 0 : ids.length - 1)
    : (index + delta + ids.length) % ids.length;
  enterRegion(root, ids[nextIndex]);
}

function focusReturn(root: HTMLElement, kind: string | null) {
  const selector = kind === 'log'
    ? '[data-gamepad-toggle="log"]'
    : kind === 'catalog'
      ? '[data-gamepad-open-region="catalog"]'
      : null;
  if (!selector) return;
  window.requestAnimationFrame(() => {
    if (!root.isConnected) return;
    const target = root.querySelector<HTMLElement>(selector);
    if (target && isGamepadFocusable(target)) setGamepadFocus(target);
  });
}

function dismissOverlay(root: HTMLElement, overlay: HTMLElement) {
  const dismiss = overlay.querySelector<HTMLElement>('[data-gamepad-dismiss]');
  const kind = overlay.getAttribute('data-gamepad-overlay');
  if (dismiss && !dismiss.hasAttribute('disabled')) dismiss.click();
  focusReturn(root, kind);
}

function parentFocusable(focus: HTMLElement): HTMLElement | null {
  const region = focus.closest('[data-gamepad-region]');
  let parent = focus.parentElement;
  while (parent && parent !== region) {
    if (parent instanceof HTMLElement && isGamepadFocusable(parent)) return parent;
    parent = parent.parentElement;
  }
  return null;
}

function handleBack(root: HTMLElement) {
  const traps = trappingOverlays(root);
  if (traps.length > 0) {
    dismissOverlay(root, traps[traps.length - 1]);
    return;
  }
  const focus = currentFocus(root);
  const overlay = focus?.closest('[data-gamepad-overlay]');
  if (overlay instanceof HTMLElement && root.contains(overlay)) {
    dismissOverlay(root, overlay);
    return;
  }
  if (!focus) return;
  const parent = parentFocusable(focus);
  if (parent) {
    setGamepadFocus(parent);
    return;
  }
  const parentId = focus.closest('[data-gamepad-region]')?.getAttribute('data-gamepad-parent');
  if (parentId) enterRegion(root, parentId);
}

function handleActivate(root: HTMLElement, options: GamepadNavOptions) {
  const scope = activeScope(root);
  let focus = currentFocus(root);
  if (!focus || !scope.contains(focus)) {
    const first = preferredStart(root, scope, options);
    if (first) setGamepadFocus(first);
    return;
  }
  const target = activationTarget(focus);
  target?.click();
}

function handleMove(root: HTMLElement, direction: Direction, options: GamepadNavOptions) {
  const scope = activeScope(root);
  const focus = currentFocus(root);
  if (!focus || !scope.contains(focus)) {
    const first = preferredStart(root, scope, options);
    if (first) setGamepadFocus(first);
    return;
  }
  const elements = listFocusables(scope).filter((element) => element !== focus);
  const candidates = elements.map((element, index) => ({
    id: String(index),
    rect: rectOf(element),
    containsCurrent: element.contains(focus),
    insideCurrent: focus.contains(element),
  }));
  const nextId = chooseSpatialTarget(rectOf(focus), candidates, direction);
  if (nextId == null) return;
  const next = elements[Number(nextId)];
  if (next) setGamepadFocus(next);
}

function canScroll(element: HTMLElement, axis: 'x' | 'y'): boolean {
  const style = window.getComputedStyle(element);
  const overflow = axis === 'y' ? style.overflowY : style.overflowX;
  if (overflow !== 'auto' && overflow !== 'scroll' && overflow !== 'overlay') return false;
  if (axis === 'y') return element.scrollHeight > element.clientHeight + 2;
  return element.scrollWidth > element.clientWidth + 2;
}

function nearestScroller(start: HTMLElement, boundary: HTMLElement): HTMLElement | null {
  let element: HTMLElement | null = start;
  while (element) {
    if (canScroll(element, 'y') || canScroll(element, 'x')) return element;
    if (element === boundary) break;
    element = element.parentElement;
  }
  return null;
}

function handleScroll(root: HTMLElement, x: number, y: number) {
  if (x === 0 && y === 0) return;
  const scope = activeScope(root);
  const focus = currentFocus(root);
  const start = focus && scope.contains(focus) ? focus : scope;
  const scroller = nearestScroller(start, scope) ?? (scope !== root ? nearestScroller(scope, root) : null);
  scroller?.scrollBy({ left: x * SCROLL_STEP, top: y * SCROLL_STEP, behavior: 'auto' });
}

function handleStart(root: HTMLElement) {
  const trap = trappingOverlays(root).pop();
  if (trap?.getAttribute('data-gamepad-overlay') === 'modal') return;
  const toggle = root.querySelector<HTMLElement>('[data-gamepad-toggle="log"]');
  if (!toggle || toggle.hasAttribute('disabled')) return;
  const wasOpen = Boolean(root.querySelector('[data-gamepad-overlay="log"]'));
  toggle.click();
  window.requestAnimationFrame(() => {
    if (!root.isConnected) return;
    if (!wasOpen) {
      const drawer = root.querySelector<HTMLElement>('[data-gamepad-overlay="log"]');
      if (drawer) focusInside(drawer);
      return;
    }
    if (isGamepadFocusable(toggle)) setGamepadFocus(toggle);
  });
}

function isActiveRoot(root: HTMLElement): boolean {
  if (!root.isConnected) return false;
  const roots = Array.from(document.querySelectorAll<HTMLElement>('[data-gamepad-root]'))
    .filter((element) => element.isConnected && isShown(element));
  return roots[roots.length - 1] === root;
}

function showHint(text = HINT_TEXT) {
  document.querySelectorAll('.gamepad-hint').forEach((node) => node.remove());
  const hint = document.createElement('p');
  hint.className = 'gamepad-hint';
  hint.setAttribute('role', 'status');
  hint.textContent = text;
  document.body.appendChild(hint);
  window.setTimeout(() => hint.remove(), 3400);
}

function applyOverviewStep(root: HTMLElement, step: OverviewStep) {
  const clicked = Boolean(step.click && !step.click.hasAttribute('disabled'));
  if (clicked) step.click?.click();
  const finish = () => {
    if (!root.isConnected) return;
    if (step.follow === 'roster') {
      const row = selectedRosterRow(root);
      if (row) setGamepadFocus(row);
      return;
    }
    if (step.follow === 'zone') {
      const zone = sheetStops(root)[0];
      if (zone) {
        setGamepadFocus(zone);
        return;
      }
      const row = selectedRosterRow(root);
      if (row) setGamepadFocus(row);
      return;
    }
    if (step.focus && isGamepadFocusable(step.focus)) setGamepadFocus(step.focus);
  };
  if (clicked) window.requestAnimationFrame(finish);
  else finish();
}

const catalogReturnFocus = new WeakMap<HTMLElement, HTMLElement>();
const sheetAnchors = new WeakMap<HTMLElement, SheetAnchor>();

function settleSheetAnchor(root: HTMLElement) {
  const anchor = sheetAnchors.get(root);
  if (!anchor) return;
  if (trappingOverlays(root).some((overlay) => overlay.getAttribute('data-gamepad-overlay') === 'modal')) return;
  const wanted = focusAfterSheetAction(root, anchor);
  if (!wanted) return;
  const focus = currentFocus(root);
  if (focus === wanted) return;
  if (
    focus
    && wanted.contains(focus)
    && focus.getAttribute('data-gamepad-action') === anchor.action
    && isGamepadFocusable(focus)
  ) return;
  setGamepadFocus(wanted);
}

type LegendKey = { glyph: string; label: string; kind: 'shoulder' | 'a' | 'y' | 'view' };

function legendKeys(profile: GamepadNavOptions['profile']): LegendKey[] {
  if (profile === 'combat') {
    return [
      { glyph: 'LB', label: 'Helden', kind: 'shoulder' },
      { glyph: 'RB', label: 'Gegner', kind: 'shoulder' },
      { glyph: 'A', label: 'Bestätigen', kind: 'a' },
      { glyph: 'Y', label: 'Übersicht', kind: 'y' },
      { glyph: 'View', label: 'Beenden', kind: 'view' },
    ];
  }
  if (profile === 'overview') {
    return [
      { glyph: 'LB', label: 'Charakter', kind: 'shoulder' },
      { glyph: 'RB', label: 'Katalog', kind: 'shoulder' },
      { glyph: 'A', label: 'Bestätigen', kind: 'a' },
      { glyph: 'Y', label: 'Kampf', kind: 'y' },
    ];
  }
  return [];
}

function syncGamepadLegend(root: HTMLElement, connected: boolean, profile: GamepadNavOptions['profile']) {
  const existing = root.querySelector<HTMLElement>(':scope > .gamepad-legend');
  const keys = connected ? legendKeys(profile) : [];
  if (keys.length === 0) {
    existing?.remove();
    return;
  }
  if (existing?.getAttribute('data-profile') === profile) return;
  existing?.remove();
  const bar = document.createElement('div');
  bar.className = 'gamepad-legend';
  bar.setAttribute('data-profile', profile || '');
  bar.setAttribute('role', 'list');
  bar.setAttribute('aria-label', 'Controller');
  keys.forEach((key) => {
    const item = document.createElement('span');
    item.className = 'gamepad-legend-item';
    item.setAttribute('role', 'listitem');
    const glyph = document.createElement('span');
    glyph.className = `pad-key pad-key-${key.kind}`;
    glyph.textContent = key.glyph;
    const label = document.createElement('span');
    label.textContent = key.label;
    item.append(glyph, label);
    bar.append(item);
  });
  root.append(bar);
}

function closeOverviewCatalog(root: HTMLElement) {
  const open = root.querySelector<HTMLElement>('[data-gamepad-overlay="catalog"]');
  if (!open || !isShown(open)) return;
  const dismiss = open.querySelector<HTMLElement>('[data-gamepad-dismiss]');
  const restore = catalogReturnFocus.get(root) ?? null;
  catalogReturnFocus.delete(root);
  if (dismiss && !dismiss.hasAttribute('disabled')) dismiss.click();
  window.requestAnimationFrame(() => {
    if (!root.isConnected) return;
    if (restore && restore.isConnected && isGamepadFocusable(restore)) {
      setGamepadFocus(restore);
      return;
    }
    const row = selectedRosterRow(root);
    if (row) setGamepadFocus(row);
  });
}

function toggleOverviewCatalog(root: HTMLElement) {
  const open = root.querySelector<HTMLElement>('[data-gamepad-overlay="catalog"]');
  if (open && isShown(open)) {
    closeOverviewCatalog(root);
    return;
  }
  const opener = root.querySelector<HTMLElement>('[data-gamepad-open-region="catalog"]');
  if (!opener || opener.hasAttribute('disabled')) return;
  const focus = currentFocus(root);
  if (focus) catalogReturnFocus.set(root, focus);
  opener.click();
  window.requestAnimationFrame(() => {
    if (!root.isConnected) return;
    const drawer = root.querySelector<HTMLElement>('[data-gamepad-overlay="catalog"]');
    if (drawer) focusInside(drawer);
  });
}

function focusOverviewStart(root: HTMLElement) {
  const trap = trappingOverlays(root).pop();
  if (trap) {
    focusInside(trap);
    return;
  }
  const row = selectedRosterRow(root);
  if (!row) return;
  setGamepadFocus(row);
  row.scrollIntoView({ block: 'start', inline: 'nearest' });
}

function handleOverviewInput(
  root: HTMLElement,
  frame: { edges: number[]; move: boolean; direction: Direction | null },
  stick: { locked: boolean },
) {
  const trap = trappingOverlays(root).pop();
  if (trap) {
    const kind = trap.getAttribute('data-gamepad-overlay');
    if (frame.edges.includes(BUTTON.b)) {
      if (kind === 'catalog') closeOverviewCatalog(root);
      else handleBack(root);
    }
    if (frame.edges.includes(BUTTON.a)) handleActivate(root, {});
    if (frame.edges.includes(BUTTON.rb) && kind === 'catalog') closeOverviewCatalog(root);
    if (frame.move && frame.direction) handleMove(root, frame.direction, {});
    return;
  }
  const pressedA = frame.edges.includes(BUTTON.a);
  if (frame.edges.includes(BUTTON.b) || frame.edges.includes(BUTTON.lb) || frame.edges.includes(BUTTON.rb)) {
    sheetAnchors.delete(root);
    stick.locked = false;
  }
  if (frame.edges.includes(BUTTON.b)) {
    const focus = currentFocus(root);
    const back = focus ? overviewBack(root, focus) : null;
    if (back) setGamepadFocus(back);
  }
  if (pressedA) {
    const step = overviewActivate(root, currentFocus(root));
    if (step.anchor && step.click && !step.click.hasAttribute('disabled')) {
      sheetAnchors.set(root, step.anchor);
      stick.locked = true;
    }
    applyOverviewStep(root, step);
  }
  settleSheetAnchor(root);
  if (stick.locked) {
    if (!frame.direction) stick.locked = false;
  }
  if (frame.edges.includes(BUTTON.lb)) {
    const focus = currentFocus(root);
    const inSheet = Boolean(focus?.closest('[data-gamepad-region="sheet"]'));
    const next = nextRosterRow(root, 1);
    if (next) applyOverviewStep(root, { click: next, follow: inSheet ? 'zone' : 'roster' });
  }
  if (frame.edges.includes(BUTTON.rb)) toggleOverviewCatalog(root);
  if (!stick.locked && frame.move && frame.direction) {
    sheetAnchors.delete(root);
    const focus = currentFocus(root);
    if (!focus) {
      const first = selectedRosterRow(root) ?? rosterRowsFallback(root);
      if (first) setGamepadFocus(first);
      return;
    }
    const step = overviewMoveTarget(root, focus, frame.direction);
    if (step) applyOverviewStep(root, step);
  }
}

function rosterRowsFallback(root: HTMLElement): HTMLElement | null {
  return root.querySelector<HTMLElement>('[data-gamepad-roster-row]');
}

const combatReturnPending = new WeakMap<HTMLElement, boolean>();
const burnAnchors = new WeakMap<HTMLElement, { key: string; role: string }>();

function settleBurnAnchor(root: HTMLElement) {
  const anchor = burnAnchors.get(root);
  if (!anchor) return;
  const wanted = focusAfterBurn(root, anchor.key, anchor.role);
  if (!wanted) return;
  const focus = currentFocus(root);
  if (focus === wanted) return;
  setGamepadFocus(wanted);
}

function applyCombatStep(root: HTMLElement, step: CombatStep) {
  const clicked = Boolean(step.click && !step.click.hasAttribute('disabled'));
  if (clicked) step.click?.click();
  if (step.follow === 'idle' && isCombatAiming(root)) combatReturnPending.set(root, true);
  const finish = (attempt = 0) => {
    if (!root.isConnected || !step.follow || step.follow === 'idle') return;
    const next = step.follow === 'burn'
      ? focusAfterBurn(root, step.burnKey || null, step.burnRole || null)
      : combatResolveFollow(root, step.follow, step.aim);
    if (next) {
      setGamepadFocus(next);
      return;
    }
    if (attempt < 2 && (step.follow === 'actions' || step.follow === 'target' || step.follow === 'burn')) {
      window.requestAnimationFrame(() => finish(attempt + 1));
    }
  };
  if (clicked) window.requestAnimationFrame(() => finish());
  else finish();
}

function settleCombatReturn(root: HTMLElement) {
  if (!combatReturnPending.get(root) || isCombatAiming(root)) return;
  combatReturnPending.delete(root);
  const home = combatHome(root);
  if (home) setGamepadFocus(home);
}

function syncCombatAimFocus(
  root: HTMLElement,
  direction: Direction | null,
  state: { wasAiming: boolean; stickLocked: boolean },
): boolean {
  const aiming = isCombatAiming(root);
  if (state.wasAiming && !aiming) {
    const focusHome = () => {
      if (!root.isConnected || isCombatAiming(root)) return;
      const home = combatHome(root);
      if (home) setGamepadFocus(home);
    };
    focusHome();
    window.requestAnimationFrame(focusHome);
    state.stickLocked = true;
  }
  state.wasAiming = aiming;
  if (state.stickLocked && !direction) state.stickLocked = false;
  return state.stickLocked;
}

function canEndCombat(root: HTMLElement): boolean {
  const trap = trappingOverlays(root).pop();
  if (trap) return false;
  const button = root.querySelector<HTMLElement>('[data-gamepad-end-combat]');
  return Boolean(button && !button.hasAttribute('disabled') && isShown(button));
}

function watchCombatEndHold(
  root: HTMLElement,
  held: boolean,
  hold: { started: number | null; fired: boolean; hinted: boolean },
) {
  if (!held) {
    hold.started = null;
    hold.fired = false;
    hold.hinted = false;
    return;
  }
  if (hold.fired) return;
  if (!canEndCombat(root)) {
    hold.started = null;
    hold.hinted = false;
    return;
  }
  const now = Date.now();
  if (hold.started == null) hold.started = now;
  const action = combatEndHoldAction(true, now - hold.started, true);
  if (action === 'hint' && !hold.hinted) {
    hold.hinted = true;
    showHint('View weiter halten, um den Kampf zu beenden');
    return;
  }
  if (action === 'end') {
    hold.fired = true;
    root.querySelector<HTMLElement>('[data-gamepad-end-combat]')?.click();
  }
}

function activateModeSwitch(root: HTMLElement) {
  const trap = trappingOverlays(root).pop();
  if (trap?.getAttribute('data-gamepad-overlay') === 'modal') return;
  const mode = root.querySelector<HTMLElement>('[data-gamepad-mode]');
  if (!mode || mode.hasAttribute('disabled')) return;
  mode.click();
}

function focusCombatStart(root: HTMLElement) {
  const trap = trappingOverlays(root).pop();
  if (trap) {
    focusInside(trap);
    return;
  }
  const hero = combatCards(root, 'heroes')[0];
  if (hero) setGamepadFocus(hero);
}

function handleCombatInput(
  root: HTMLElement,
  frame: { edges: number[]; move: boolean; direction: Direction | null },
  aim: { wasAiming: boolean; stickLocked: boolean },
  burnStick: { locked: boolean },
) {
  const stickLocked = syncCombatAimFocus(root, frame.direction, aim);
  settleCombatReturn(root);
  const trap = trappingOverlays(root).pop();
  if (trap) {
    if (frame.edges.includes(BUTTON.b)) handleBack(root);
    if (frame.edges.includes(BUTTON.a)) handleActivate(root, {});
    if (frame.move && frame.direction) handleMove(root, frame.direction, {});
    return;
  }
  if (frame.edges.includes(BUTTON.b) || frame.edges.includes(BUTTON.lb) || frame.edges.includes(BUTTON.rb)) {
    burnAnchors.delete(root);
    burnStick.locked = false;
  }
  if (frame.edges.includes(BUTTON.b)) {
    combatReturnPending.delete(root);
    const focus = currentFocus(root);
    if (focus?.matches('[data-gamepad-combat-action]')) {
      const picked = pickedCombatAction(root);
      if (picked) picked.click();
    }
    const back = focus ? combatBack(root, focus) : combatActorFocus(root);
    if (back) {
      const restore = () => {
        if (back.isConnected && isGamepadFocusable(back)) setGamepadFocus(back);
        else {
          const actor = combatActorFocus(root);
          if (actor) setGamepadFocus(actor);
        }
      };
      if (focus?.matches('[data-gamepad-combat-action]') && pickedCombatAction(root)) {
        window.requestAnimationFrame(restore);
      } else {
        restore();
      }
    }
  }
  if (frame.edges.includes(BUTTON.a)) {
    const step = combatActivate(root, currentFocus(root));
    if (step.follow === 'burn') {
      burnAnchors.set(root, { key: step.burnKey || '', role: step.burnRole || '' });
      burnStick.locked = true;
    }
    applyCombatStep(root, step);
  }
  settleBurnAnchor(root);
  if (burnStick.locked && !frame.direction) burnStick.locked = false;
  if (frame.edges.includes(BUTTON.lb)) {
    const next = combatSideEntry(root, 'heroes', currentFocus(root));
    if (next) setGamepadFocus(next);
  }
  if (frame.edges.includes(BUTTON.rb)) {
    const next = combatSideEntry(root, 'foes', currentFocus(root));
    if (next) setGamepadFocus(next);
  }
  if (!stickLocked && !burnStick.locked && frame.move && frame.direction) {
    burnAnchors.delete(root);
    const focus = currentFocus(root);
    if (!focus) {
      const hero = combatCards(root, 'heroes')[0];
      if (hero) setGamepadFocus(hero);
      return;
    }
    const next = combatMoveTarget(root, focus, frame.direction);
    if (next) setGamepadFocus(next);
  }
}

export function attachGamepadNavigation(root: HTMLElement, optionsSource: OptionsSource): () => void {
  let hintLock = 0;
  let wasConnected = false;
  const endHold = { started: null as number | null, fired: false, hinted: false };
  const combatAim = { wasAiming: false, stickLocked: false };
  const burnStick = { locked: false };
  const sheetStick = { locked: false };
  const announce = () => {
    const now = Date.now();
    if (now < hintLock) return;
    hintLock = now + 3200;
    showHint(optionsSource().hint || HINT_TEXT);
  };
  const onConnect = () => {
    if (!isActiveRoot(root)) return;
    const profile = optionsSource().profile;
    if (profile !== 'overview' && profile !== 'combat') announce();
  };
  const onFocusIn = (event: FocusEvent) => {
    if (!wasConnected) return;
    const target = event.target;
    if (!(target instanceof HTMLElement) || !root.contains(target) || !isGamepadFocusable(target)) return;
    root.querySelectorAll(FOCUS_SELECTOR).forEach((node) => {
      if (node !== target) clearMarker(node);
    });
    markFocused(target);
  };
  window.addEventListener('gamepadconnected', onConnect);
  root.addEventListener('focusin', onFocusIn);
  const unsubscribe = subscribeGamepad((frame) => {
    if (!root.isConnected || !isActiveRoot(root)) return;
    const connected = Boolean(frame.pad);
    if (!connected && wasConnected) clearFocusRing(root);
    const options = optionsSource() || {};
    syncGamepadLegend(root, connected, options.profile);
    if (connected && !wasConnected) {
      if (options.profile !== 'overview' && options.profile !== 'combat') announce();
      if (options.profile === 'overview') focusOverviewStart(root);
      if (options.profile === 'combat') focusCombatStart(root);
    }
    wasConnected = connected;
    if (!connected) {
      endHold.started = null;
      endHold.fired = false;
      endHold.hinted = false;
      combatAim.wasAiming = false;
      combatAim.stickLocked = false;
      sheetStick.locked = false;
      burnStick.locked = false;
      sheetAnchors.delete(root);
      burnAnchors.delete(root);
      return;
    }
    if (options.profile === 'combat') {
      watchCombatEndHold(root, Boolean(frame.pad?.buttons[BUTTON.back]), endHold);
    }
    if ((options.profile === 'overview' || options.profile === 'combat') && frame.edges.includes(BUTTON.y)) {
      activateModeSwitch(root);
    }
    if (options.profile === 'combat' && combatCards(root).length > 0) {
      handleCombatInput(root, frame, combatAim, burnStick);
      if (frame.edges.includes(BUTTON.start)) handleStart(root);
      if (frame.scrollX !== 0 || frame.scrollY !== 0) handleScroll(root, frame.scrollX, frame.scrollY);
      return;
    }
    if (options.profile === 'overview') {
      handleOverviewInput(root, frame, sheetStick);
      if (frame.edges.includes(BUTTON.start)) handleStart(root);
      if (frame.scrollX !== 0 || frame.scrollY !== 0) handleScroll(root, frame.scrollX, frame.scrollY);
      return;
    }
    if (frame.edges.includes(BUTTON.b)) handleBack(root);
    if (frame.edges.includes(BUTTON.a)) handleActivate(root, options);
    if (frame.edges.includes(BUTTON.lb)) cycleRegion(root, options, -1);
    if (frame.edges.includes(BUTTON.rb)) cycleRegion(root, options, 1);
    if (frame.edges.includes(BUTTON.start)) handleStart(root);
    if (frame.move && frame.direction) handleMove(root, frame.direction, options);
    if (frame.scrollX !== 0 || frame.scrollY !== 0) handleScroll(root, frame.scrollX, frame.scrollY);
  });
  return () => {
    window.removeEventListener('gamepadconnected', onConnect);
    root.removeEventListener('focusin', onFocusIn);
    unsubscribe();
    sheetAnchors.delete(root);
    if (root.isConnected) {
      clearFocusRing(root);
      root.querySelector(':scope > .gamepad-legend')?.remove();
    }
  };
}

export function useGamepadNavigation(options?: GamepadNavOptions) {
  const optionsRef = useRef<GamepadNavOptions>(options ?? {});
  optionsRef.current = options ?? {};
  const [node, setNode] = useState<HTMLElement | null>(null);

  const ref = useCallback((next: HTMLElement | null) => {
    setNode((current) => (current === next ? current : next));
  }, []);

  useEffect(() => {
    if (!node) return undefined;
    return attachGamepadNavigation(node, () => optionsRef.current);
  }, [node]);

  return ref;
}
